/**
 * How big the monitor writes.
 *
 * Four sizes, chosen on the screen and remembered on that screen. The default
 * is the size the board has always had: turning it up is a choice a kitchen
 * makes, not something that happens to them mid-service.
 *
 * The sizes themselves live in CSS custom properties (see `index.css`), so
 * switching is one attribute on the root element and everything — cards,
 * modals, station columns — follows at once.
 */

import type { KdsT } from "./i18n/settings";

/**
 * Smallest first: the order is the order the button cycles through and the
 * order the card widths grow in. `piccolo` exists for a kitchen that would
 * rather see more tables at once than read them from across the room — Sun
 * Club asked for it on 23/09/2026.
 */
export const KDS_SCALES = ["piccolo", "normale", "grande", "enorme"] as const;
export type KdsScale = (typeof KDS_SCALES)[number];

/** The size the board had before any of this existed. */
export const DEFAULT_KDS_SCALE: KdsScale = "normale";

/** Per-screen: the monitor by the pass and the one at the bar can differ. */
export const KDS_SCALE_STORAGE_KEY = "voxli.kds.scale";

/** What the button says, in the order the button cycles through them. */
export const KDS_SCALE_LABEL: Record<KdsScale, (t: KdsT) => string> = {
  piccolo: (t) => t("kds.scale.small", "Piccolo"),
  normale: (t) => t("kds.scale.normal", "Normale"),
  grande: (t) => t("kds.scale.large", "Grande"),
  enorme: (t) => t("kds.scale.extraLarge", "Molto grande"),
};

/**
 * Anything unreadable — a value from an older build, a hand-edited entry, an
 * empty string — falls back to the default rather than leaving the board
 * unstyled.
 */
export function parseKdsScale(value: unknown): KdsScale {
  return KDS_SCALES.includes(value as KdsScale) ? (value as KdsScale) : DEFAULT_KDS_SCALE;
}

/** The next size round the loop. One button, no menu to get lost in. */
export function nextKdsScale(current: KdsScale): KdsScale {
  const index = KDS_SCALES.indexOf(current);
  return KDS_SCALES[(index + 1) % KDS_SCALES.length];
}

/**
 * How wide a table card is at this size. A dish line that wraps onto two lines
 * costs more vertical space than the wider card costs horizontally, so the card
 * grows with the type.
 */
export const KDS_CARD_WIDTH: Record<KdsScale, { min: string; max: string }> = {
  piccolo: { min: "250px", max: "300px" },
  normale: { min: "320px", max: "400px" },
  grande: { min: "460px", max: "580px" },
  enorme: { min: "560px", max: "700px" },
};

/** How far one press of the board's ◀ ▶ moves: one card. */
export function boardScrollStep(scale: KdsScale): number {
  return parseInt(KDS_CARD_WIDTH[scale].min, 10);
}
